// ─────────────────────────────────────────────────────────────────────────────
// LabThree — Centro assistenza (container)
//
// Voce sidebar "Centro assistenza" → atterra qui. Mostra l'elenco delle guide
// disponibili. Cliccando una guida si apre il componente specifico (per ora
// solo "Primi passi"), con un breadcrumb di ritorno all'indice.
//
// Estendere a future guide:
//   1. crea un nuovo file in src/help/guide/<NomeGuida>.js esportando default
//      un componente React
//   2. aggiungilo all'array GUIDE qui sotto con id, titolo, sottotitolo,
//      durata, categoria (per il CatTile colorato), icona del lucchetto se
//      è una guida riservata, e component (lazy import o import diretto).
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect } from 'react';
import LT from '../ui/tokens';
import I, { Ico } from '../ui/icons';
import { Chip, CatTile } from '../ui/components';
import PrimiPassi from './guide/PrimiPassi';
import PrimoGiorno from './guide/PrimoGiorno';
import Monitoraggi from './guide/Monitoraggi';
import NonConformita from './guide/NonConformita';
import DocumentiHaccp from './guide/DocumentiHaccp';
import Verifiche from './guide/Verifiche';
import Storico from './guide/Storico';
import Organico from './guide/Organico';
import Oggetti from './guide/Oggetti';
import Scadenziario from './guide/Scadenziario';
import MiaAzienda from './guide/MiaAzienda';
import PrimiPassiTracciabilita from './guide/PrimiPassiTracciabilita';
import AnagraficheFiliera from './guide/AnagraficheFiliera';
import IngredientiAllergeni from './guide/IngredientiAllergeni';
import ProdottiSemilavorati from './guide/ProdottiSemilavorati';
import CreareRicetta from './guide/CreareRicetta';
import Accettazioni from './guide/Accettazioni';
import MagazzinoMp from './guide/MagazzinoMp';
import Lavorazione from './guide/Lavorazione';
import Giacenze from './guide/Giacenze';
import ProdottiFinitiUscite from './guide/ProdottiFinitiUscite';
import BilancioMassa from './guide/BilancioMassa';
import TestTracciabilita from './guide/TestTracciabilita';

// Catalogo guide. Aggiungi qui le nuove guide in futuro.
const GUIDE = [
  {
    id: 'primi-passi',
    titolo: 'Primi passi con LabThree',
    sottotitolo: 'Configura il workspace HACCP in 4 mosse: operatori, oggetti, scadenziario, monitoraggi.',
    durata: '~ 10 minuti',
    cat: 'formazione',
    audience: 'Per amministratori',
    badge: 'Inizia da qui',
    badgeKind: 'verde',
    Component: PrimiPassi,
  },
  {
    id: 'primo-giorno',
    titolo: 'Il primo giorno con LabThree',
    sottotitolo: 'Entrare col PIN, leggere la schermata delle attività, registrare il primo controllo e correggere un errore.',
    durata: '~ 12 minuti',
    cat: 'produzione',
    audience: 'Per operatori',
    badge: 'Per chi inizia',
    badgeKind: 'blu',
    Component: PrimoGiorno,
  },
  {
    id: 'monitoraggi',
    titolo: 'Registrare i monitoraggi',
    sottotitolo: "La lista di ogni mattina: i tre pulsanti, il valore misurato, la sessione per registrare un giro fatto prima, e come si corregge uno sbaglio.",
    durata: '~ 12 minuti',
    cat: 'temperature',
    audience: 'Per operatori',
    Component: Monitoraggi,
  },
  {
    id: 'non-conformita',
    titolo: 'Gestire una non conformità',
    sottotitolo: "Aprirla, descriverla in modo utile, registrare l'azione correttiva e chiuderla. La parte che un ispettore guarda per prima.",
    durata: '~ 15 minuti',
    cat: 'pulizie',
    audience: 'Per operatori e amministratori',
    Component: NonConformita,
  },
  {
    id: 'documenti-haccp',
    titolo: 'Digitalizzare i documenti HACCP',
    sottotitolo: 'Portare dentro LabThree manuale, procedure e piani, e tenerne sotto controllo le revisioni.',
    durata: '~ 12 minuti',
    cat: 'formazione',
    audience: 'Per amministratori',
    Component: DocumentiHaccp,
  },
  {
    id: 'verifiche',
    titolo: 'Le Verifiche: chi controlla i controlli',
    sottotitolo: "La firma di seconda mano: chi la può dare, la coda delle verifiche singole e la verifica a blocchi per chiudere un periodo intero.",
    durata: '~ 10 minuti',
    cat: 'formazione',
    audience: 'Per amministratori e responsabili',
    Component: Verifiche,
  },
  {
    id: 'storico-stampe',
    titolo: 'Lo Storico e le stampe',
    sottotitolo: "Cercare nel registro, correggere con motivazione, e stampare il Registro monitoraggi HACCP con firme e totali per l'ispezione.",
    durata: '~ 12 minuti',
    cat: 'produzione',
    audience: 'Per amministratori e responsabili',
    Component: Storico,
  },
  {
    id: 'organico-pin',
    titolo: 'Organico e PIN',
    sottotitolo: "Inserire le persone, i PIN personali, la firma grafica per le stampe, e la strada giusta quando qualcuno lascia l'azienda.",
    durata: '~ 10 minuti',
    cat: 'formazione',
    audience: 'Per amministratori',
    Component: Organico,
  },
  {
    id: 'oggetti',
    titolo: 'Oggetti da monitorare',
    sottotitolo: "Celle, attrezzature e ambienti: come crearli con la giusta granularità, la scheda oggetto, e il rendi obsoleto al posto del cancella.",
    durata: '~ 9 minuti',
    cat: 'manutenzioni',
    audience: 'Per amministratori',
    Component: Oggetti,
  },
  {
    id: 'scadenziario',
    titolo: 'Lo Scadenziario, il motore di tutto',
    sottotitolo: "Chi controlla cosa, ogni quanto e con quali soglie: la frequenza, le persone, i doppioni — e la formazione a scadenziario per le realtà piccole.",
    durata: '~ 13 minuti',
    cat: 'produzione',
    audience: 'Per amministratori',
    Component: Scadenziario,
  },
  {
    id: 'mia-azienda',
    titolo: 'La mia azienda',
    sottotitolo: "Dati, logo e abbonamento: la scheda che firma la testata di ogni stampa, più il cartello QR da appendere in produzione.",
    durata: '~ 7 minuti',
    cat: 'formazione',
    audience: 'Per amministratori',
    Component: MiaAzienda,
  },
  {
    id: 'primi-passi-tracciabilita',
    titolo: 'Primi passi con la Tracciabilità',
    sottotitolo: "La mappa del modulo, le parole da conoscere e l'ordine in cui si fanno le cose. Da leggere prima di toccare le schermate.",
    durata: '~ 15 minuti',
    cat: 'produzione',
    audience: 'Per amministratori · TRACE',
    badge: 'Inizia da qui',
    badgeKind: 'verde',
    Component: PrimiPassiTracciabilita,
  },
  {
    id: 'anagrafiche-filiera',
    titolo: 'Le anagrafiche di filiera',
    sottotitolo: 'Fornitori, clienti e ubicazioni: cosa serve davvero compilare, la qualifica dei fornitori e i contatti per il richiamo.',
    durata: '~ 12 minuti',
    cat: 'temperature',
    audience: 'Per amministratori · TRACE',
    Component: AnagraficheFiliera,
  },
  {
    id: 'ingredienti-allergeni',
    titolo: 'Ingredienti, materie prime e allergeni',
    sottotitolo: "Le cinque categorie, l'unità di misura che si trascina dietro tutto, i quattordici allergeni e la ricerca automatica dei valori nutrizionali.",
    durata: '~ 12 minuti',
    cat: 'pulizie',
    audience: 'Per amministratori · TRACE',
    Component: IngredientiAllergeni,
  },
  {
    id: 'prodotti-semilavorati',
    titolo: 'Prodotti e semilavorati',
    sottotitolo: 'Cosa cambia fra un prodotto finito e un semilavorato, e perché da quella scelta dipende quanto lontano arriva la tracciabilità.',
    durata: '~ 8 minuti',
    cat: 'produzione',
    audience: 'Per amministratori · TRACE',
    Component: ProdottiSemilavorati,
  },
  {
    id: 'creare-ricetta',
    titolo: 'Creare una ricetta',
    sottotitolo: 'La quantità prevista che riscala tutto, quante fasi servono davvero, i semilavorati e i dati nutrizionali calcolati.',
    durata: '~ 18 minuti',
    cat: 'manutenzioni',
    audience: 'Per amministratori · TRACE',
    Component: CreareRicetta,
  },
  {
    id: 'accettazioni',
    titolo: 'Registrare una consegna',
    sottotitolo: "La bolla di ingresso e i controlli al ricevimento in un gesto solo: chi ha consegnato, cosa contiene, com'era quando è arrivata.",
    durata: '~ 15 minuti',
    cat: 'temperature',
    audience: 'Per operatori e amministratori · TRACE',
    Component: Accettazioni,
  },
  {
    id: 'magazzino-mp',
    titolo: 'Il magazzino materie prime',
    sottotitolo: 'Gli stati di un lotto, le scadenze, la disponibilità stimata e come si corregge un dato sbagliato al ricevimento.',
    durata: '~ 12 minuti',
    cat: 'pulizie',
    audience: 'Per operatori e amministratori · TRACE',
    Component: MagazzinoMp,
  },
  {
    id: 'lavorazione',
    titolo: "Una lavorazione dall'inizio alla fine",
    sottotitolo: 'Aprirla, scegliere i lotti, portarla avanti fase per fase e chiuderla dichiarando dove resta il prodotto. Il cuore della tracciabilità.',
    durata: '~ 25 minuti',
    cat: 'produzione',
    audience: 'Per operatori e amministratori · TRACE',
    Component: Lavorazione,
  },
  {
    id: 'giacenze',
    titolo: 'Giacenze per ubicazione',
    sottotitolo: "Dove si trova adesso ogni lotto, cella per cella, e come si sposta da una all'altra senza entrare nella lavorazione.",
    durata: '~ 10 minuti',
    cat: 'produzione',
    audience: 'Per operatori e amministratori · TRACE',
    Component: Giacenze,
  },
  {
    id: 'prodotti-finiti-uscite',
    titolo: 'Magazzino prodotti finiti e bolle di uscita',
    sottotitolo: 'Il collegamento fra il lotto e il cliente: la metà della tracciabilità che serve davvero il giorno di un richiamo.',
    durata: '~ 15 minuti',
    cat: 'manutenzioni',
    audience: 'Per operatori e amministratori · TRACE',
    Component: ProdottiFinitiUscite,
  },
  {
    id: 'bilancio-massa',
    titolo: 'Il bilancio di massa',
    sottotitolo: "Quanto è entrato e quanto ne è uscito. L'unica schermata in cui il programma dà un giudizio — e su cosa lo dà davvero.",
    durata: '~ 12 minuti',
    cat: 'formazione',
    audience: 'Per amministratori · TRACE',
    Component: BilancioMassa,
  },
  {
    id: 'test-tracciabilita',
    titolo: 'Il test di tracciabilità',
    sottotitolo: "Da un lotto risalire a tutto quello che ci è entrato, o scendere fino ai clienti che l'hanno ricevuto. E la scheda da consegnare.",
    durata: '~ 14 minuti',
    cat: 'temperature',
    audience: 'Per amministratori · TRACE',
    Component: TestTracciabilita,
  },
  // esempio voci future (placeholder, niente Component → mostra "in arrivo"):
  // {
  //   id: 'gestione-nc',
  //   titolo: 'Gestire le non conformità',
  //   sottotitolo: 'Apertura, classificazione e chiusura di una NC.',
  //   durata: '~ 6 minuti',
  //   cat: 'pulizie',
  //   audience: 'Per amministratori',
  //   placeholder: 'In preparazione',
  // },
];

// ─────── Index (elenco guide) ────────────────────────────────────────────────

function IndiceGuide({ onApri }) {
  return (
    <div style={{ maxWidth: 1080, margin: '0 auto', padding: '0 24px 64px' }}>
      {/* Hero */}
      <div style={{
        padding: '32px 0 24px',
        borderBottom: `1px solid ${LT.n150}`,
        marginBottom: 32,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          fontSize: 12, color: LT.n500, marginBottom: 12,
        }}>
          <Ico d={I.info} size={14} />
          <span style={{ fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Centro assistenza
          </span>
        </div>
        <h1 style={{
          fontSize: 36, fontWeight: 700, color: LT.n900,
          letterSpacing: '-0.02em', margin: '0 0 10px', lineHeight: 1.15,
        }}>
          Guide e tutorial
        </h1>
        <p style={{ fontSize: 16, color: LT.n500, lineHeight: 1.6, margin: 0, maxWidth: 720 }}>
          Articoli pratici per imparare a usare LabThree e per risolvere le situazioni
          più comuni. Aggiorniamo l'elenco man mano che il prodotto cresce.
        </p>
      </div>

      {/* Griglia guide */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        gap: 16,
      }}>
        {GUIDE.map(g => (
          <button
            key={g.id}
            onClick={() => g.Component && onApri(g.id)}
            disabled={!g.Component}
            style={{
              textAlign: 'left',
              background: '#fff',
              border: `1px solid ${LT.n150}`,
              borderRadius: LT.r.lg,
              padding: 22,
              cursor: g.Component ? 'pointer' : 'not-allowed',
              fontFamily: LT.sans,
              transition: 'transform .15s, box-shadow .15s, border-color .15s',
              opacity: g.Component ? 1 : 0.6,
            }}
            onMouseEnter={e => {
              if (!g.Component) return;
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = LT.sh3;
              e.currentTarget.style.borderColor = LT.blu + '55';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'none';
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.borderColor = LT.n150;
            }}
          >
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14,
            }}>
              <CatTile cat={g.cat} size={40} />
              {g.badge && (
                <Chip kind={g.badgeKind || 'blu'} size="sm">{g.badge}</Chip>
              )}
              {g.placeholder && (
                <Chip kind="grigio" size="sm">{g.placeholder}</Chip>
              )}
            </div>
            <div style={{
              fontSize: 17, fontWeight: 700, color: LT.n900,
              letterSpacing: '-0.01em', marginBottom: 6, lineHeight: 1.3,
            }}>
              {g.titolo}
            </div>
            <div style={{
              fontSize: 13.5, color: LT.n500, lineHeight: 1.5, marginBottom: 14,
            }}>
              {g.sottotitolo}
            </div>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, color: LT.n500,
              flexWrap: 'wrap',
            }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <Ico d={I.clock} size={13} />
                {g.durata}
              </span>
              <span style={{ color: LT.n300 }}>·</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <Ico d={I.user} size={13} />
                {g.audience}
              </span>
            </div>
          </button>
        ))}
      </div>

      {/* Box "non hai trovato" */}
      <div style={{
        marginTop: 32,
        padding: '20px 24px',
        background: LT.n50,
        border: `1px solid ${LT.n150}`,
        borderRadius: LT.r.lg,
        display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
      }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontSize: 14.5, fontWeight: 700, color: LT.n900, marginBottom: 4 }}>
            Non trovi quello che cerchi?
          </div>
          <div style={{ fontSize: 13, color: LT.n500, lineHeight: 1.55 }}>
            Scrivici e un consulente HACCP ti contatterà entro la giornata lavorativa.
          </div>
        </div>
        <a
          href="mailto:info@labthree.it?subject=Richiesta%20supporto%20LabThree"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '12px 20px', background: LT.grad, color: '#fff',
            borderRadius: LT.r.md, fontSize: 14, fontWeight: 600,
            textDecoration: 'none',
          }}
        >
          <Ico d={I.mail} size={15} stroke="#fff" />
          info@labthree.it
        </a>
      </div>
    </div>
  );
}

// ─────── Container Centro assistenza ─────────────────────────────────────────

export default function CentroAssistenza({ operatore, azienda }) {
  const [guidaApertaId, setGuidaApertaId] = useState(null);
  const guidaCorrente = guidaApertaId ? GUIDE.find(g => g.id === guidaApertaId) : null;

  // Le guide in fondo all'indice si raggiungono scorrendo, e aprendole il
  // browser resta dov'era: la guida partiva a meta'. Vale anche al ritorno,
  // dove ci si aspetta di ritrovare l'indice dall'alto.
  useEffect(() => { window.scrollTo(0, 0); }, [guidaApertaId]);

  // Vista guida aperta
  if (guidaCorrente && guidaCorrente.Component) {
    const Component = guidaCorrente.Component;
    return (
      <div style={{ background: LT.nBg, minHeight: '100%' }}>
        {/* Breadcrumb / back */}
        <div style={{
          background: '#fff',
          borderBottom: `1px solid ${LT.n150}`,
          padding: '14px 24px',
          position: 'sticky', top: 0, zIndex: 5,
        }}>
          <div style={{
            maxWidth: 1080, margin: '0 auto',
            display: 'flex', alignItems: 'center', gap: 10,
            fontSize: 13, color: LT.n500,
          }}>
            <button
              onClick={() => setGuidaApertaId(null)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '6px 10px', background: LT.n50, border: `1px solid ${LT.n150}`,
                borderRadius: LT.r.sm, fontSize: 12, color: LT.n700,
                cursor: 'pointer', fontFamily: LT.sans, fontWeight: 600,
              }}
            >
              <Ico d={I.arrowLeft} size={13} />
              Centro assistenza
            </button>
            <span style={{ color: LT.n300 }}>/</span>
            <span style={{ color: LT.n800, fontWeight: 600 }}>{guidaCorrente.titolo}</span>
          </div>
        </div>
        <Component operatore={operatore} azienda={azienda} />
      </div>
    );
  }

  // Vista indice
  return (
    <div style={{ background: LT.nBg, minHeight: '100%', paddingTop: 8 }}>
      <IndiceGuide onApri={setGuidaApertaId} />
    </div>
  );
}
